import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, Trash2, Filter } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getScreeningRules, createScreeningRule, updateScreeningRule, deleteScreeningRule, getScreeningDefaults,
} from '../api';

interface Props {
  positionId: string;
  positionName: string;
  isHr: boolean;
}

const splitLines = (s: string): string[] =>
  s.split('\n').map(x => x.trim()).filter(Boolean);

function safeJoin(val: any): string {
  if (Array.isArray(val)) return val.join('\n');
  if (typeof val === 'string') {
    try { const arr = JSON.parse(val); return Array.isArray(arr) ? arr.join('\n') : val; }
    catch { return val; }
  }
  return '';
}

/**
 * 岗位级简历筛选规则设置。复用 screening_rules（position_id 绑定本岗位）与既有 API，
 * 不新增后端接口。一个岗位保存后仅保留一条 active 规则（后端 POST 会自动停用旧的）。
 */
export default function PositionScreeningTab({ positionId, positionName, isHr }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ruleId, setRuleId] = useState<string | null>(null);
  const [defaults, setDefaults] = useState<any>(null);
  const [form, setForm] = useState({
    min_education: '本科',
    min_work_years: 0,
    target_companies: '',
    target_schools: '',
    required_keywords: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rules, def] = await Promise.all([
        getScreeningRules().catch(() => []),
        getScreeningDefaults().catch(() => null),
      ]);
      setDefaults(def);
      const rule = (Array.isArray(rules) ? rules : []).find(
        (r: any) => r.position_id === positionId && r.is_active
      );
      if (rule) {
        setRuleId(rule.id);
        setForm({
          min_education: rule.min_education || '本科',
          min_work_years: rule.min_work_years || 0,
          target_companies: safeJoin(rule.target_companies),
          target_schools: safeJoin(rule.target_schools),
          required_keywords: safeJoin(rule.required_keywords),
        });
      } else {
        setRuleId(null);
      }
    } finally {
      setLoading(false);
    }
  }, [positionId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const data = {
        position_id: positionId,
        rule_name: `${positionName} 简历筛选`,
        min_education: form.min_education,
        min_work_years: form.min_work_years,
        target_companies: splitLines(form.target_companies),
        target_schools: splitLines(form.target_schools),
        required_keywords: splitLines(form.required_keywords),
      };
      if (ruleId) {
        await updateScreeningRule(ruleId, data);
      } else {
        // 后端 POST 会先停用同岗位旧规则，再插入新规则（返回新记录）
        const created = await createScreeningRule(data);
        if (created?.id) setRuleId(created.id);
      }
      toast.success('简历筛选规则已保存');
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const clearRule = async () => {
    if (!ruleId) return;
    if (!window.confirm('确认清除本岗位的简历筛选规则？清除后将回退到系统通用规则。')) return;
    try {
      await deleteScreeningRule(ruleId);
      toast.success('已清除');
      setRuleId(null);
      setForm({ min_education: '本科', min_work_years: 0, target_companies: '', target_schools: '', required_keywords: '' });
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || '清除失败');
    }
  };

  if (loading) {
    return (
      <div className="h-40 flex items-center justify-center">
        <Loader2 size={20} className="animate-spin text-primary-600" />
      </div>
    );
  }

  const eduLevels: string[] = defaults?.education_levels || ['博士', '硕士', '本科', '大专'];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 max-w-3xl">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Filter size={15} className="text-primary-600" />简历筛选规则
        </h3>
        <span className={`text-[11px] px-2 py-0.5 rounded-full ${ruleId ? 'bg-primary-50 text-primary-600' : 'bg-gray-100 text-gray-400'}`}>
          {ruleId ? '已启用岗位规则' : '未设置（使用通用规则）'}
        </span>
      </div>
      <p className="text-[11px] text-gray-400 mb-4">
        为本岗位设置简历自动筛选条件；候选人导入/采集时会按此规则打分与判定。未设置时使用系统通用规则。
      </p>

      {!isHr ? (
        <p className="text-sm text-gray-400">仅 HR / 管理员可编辑筛选规则。</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">最低学历</label>
              <select value={form.min_education} onChange={e => setForm({ ...form, min_education: e.target.value })}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500">
                {eduLevels.map((e: string) => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">最低工作年限</label>
              <input type="number" min={0} value={form.min_work_years}
                onChange={e => setForm({ ...form, min_work_years: parseInt(e.target.value) || 0 })}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">目标公司（每行一个）</label>
            <textarea value={form.target_companies} onChange={e => setForm({ ...form, target_companies: e.target.value })}
              rows={4} placeholder={'腾讯\n字节跳动\n华为\n...'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            <p className="text-[10px] text-gray-400 mt-1">留空则使用系统默认名企列表（{defaults?.top_companies?.length || 0}家）</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">目标院校（每行一个）</label>
            <textarea value={form.target_schools} onChange={e => setForm({ ...form, target_schools: e.target.value })}
              rows={4} placeholder={'清华大学\n北京大学\n985\n...'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
            <p className="text-[10px] text-gray-400 mt-1">留空则使用系统默认名校列表（{defaults?.top_schools?.length || 0}所）</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">技能关键词（每行一个，可选）</label>
            <textarea value={form.required_keywords} onChange={e => setForm({ ...form, required_keywords: e.target.value })}
              rows={2} placeholder={'Java\nReact\n...'}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none" />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            {ruleId && (
              <button onClick={clearRule}
                className="h-9 px-3 text-sm text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1.5">
                <Trash2 size={13} />清除规则
              </button>
            )}
            <button onClick={save} disabled={saving}
              className="h-9 px-4 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50 flex items-center gap-1.5">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}保存筛选规则
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
